import type { ConnectError } from '@connectrpc/connect'
import {
  PlanSubmissionErrorReason,
  PlanSubmissionErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'
import {
  WorkflowCaseErrorReason,
  WorkflowCaseErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import type { TFunction } from 'i18next'

import {
  readAppErrorInfo,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import { formatDateTimeFull } from '~/models/dateTime'

const workflowCaseReasons: Record<
  WorkflowCaseErrorReason,
  { key: string } | undefined
> = {
  [WorkflowCaseErrorReason.UNSPECIFIED]: undefined,
  [WorkflowCaseErrorReason.APPROVER_ALL_INACTIVE]: {
    key: 'page:procurementPlanDetail.submit.approverAllInactiveDescription',
  },
  [WorkflowCaseErrorReason.WORKFLOW_CASE_DELETED]: {
    key: 'page:procurementPlanDetail.submit.workflowCaseDeletedDescription',
  },
  // issue #5219 Decisions #13/#15: TEMPLATE_REQUIRES_BG is emitted only by
  // CreateWorkflowCases (workflow-case creation), which this submit dialog
  // never calls — no dedicated message, falls back to the existing
  // code-based message.
  [WorkflowCaseErrorReason.TEMPLATE_REQUIRES_BG]: undefined,
  // AC-WFT-039 / AC-PLC-030-1: 手動完了で承認待ちの間は計画画面から提出できない。
  [WorkflowCaseErrorReason.MANUAL_COMPLETION_AWAITING_APPROVAL]: {
    key: 'page:procurementPlanDetail.submit.manualCompletionAwaitingApprovalDescription',
  },
  // SUSPENDED (AC-PLC-086): 中断したワークフローの計画の文言は未実装。
  // TODO(#6680): 文言を入れる。それまでは code ベースの fallback。
  [WorkflowCaseErrorReason.SUSPENDED]: undefined,
}

const planSubmissionReasons: Record<
  PlanSubmissionErrorReason,
  { key: string } | undefined
> = {
  [PlanSubmissionErrorReason.UNSPECIFIED]: undefined,
  [PlanSubmissionErrorReason.OCCTO_CREDENTIAL_NOT_CONFIGURED]: {
    key: 'page:procurementPlanDetail.submit.occtoCredentialNotConfiguredDescription',
  },
  // AC-PLC-055 / 056: 拒否 toast の文言は詳細画面の誘導メッセージと同じ key を
  // 共有する (spec の文言が 1 つなので単一 source にする)。
  [PlanSubmissionErrorReason.SUPERSEDED_VERSION]: {
    key: 'page:procurementPlanDetail.submitGuidance.supersededVersion',
  },
  [PlanSubmissionErrorReason.MISSING_BASELINE_BOXES]: {
    key: 'page:procurementPlanDetail.submitGuidance.missingBaselineBoxes',
  },
  // AC-PLC-085: 画面を開いた後に OCCTO の受付時間外に入った。 {{resumesAt}} は metadata の値を
  // AC-PLC-020-1 と同じ絶対日時に整形して埋める (submitInterpolation)。
  [PlanSubmissionErrorReason.DIRECT_SUBMIT_HELD]: {
    key: 'page:procurementPlanDetail.submit.directSubmitHeldDescription',
  },
}

/**
 * 拒否の metadata のうち、 そのままでは文に出せない値を表示用に直す。 `resumesAt` (RFC3339) は
 * 絶対日時 (JST) にする。 読めない値は metadata の値のまま残す。
 */
function submitInterpolation(
  err: ConnectError,
): Record<string, string> | undefined {
  const resumesAt = readAppErrorInfo(err, PlanSubmissionErrorReasonSchema)
    ?.metadata.resumesAt
  if (resumesAt === undefined) return undefined
  const date = new Date(resumesAt)
  if (Number.isNaN(date.getTime())) return undefined
  return { resumesAt: formatDateTimeFull(date) }
}

/**
 * Resolves the submit dialog's server error message from either of the two
 * domains SubmitProcurementPlan can reject with a structured
 * `google.rpc.ErrorInfo` detail:
 * WorkflowCaseErrorReason (linked workflow-case gates, e.g. deleted case /
 * inactive approvers) and PlanSubmissionErrorReason (enqueue-time
 * preconditions, e.g. missing OCCTO credential). Only one can be attached
 * to a given error, so trying both registries in order and returning the
 * first resolved message is safe.
 */
export function resolveSubmitServerError(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return (
    resolveAppErrorInfoMessage(
      err,
      WorkflowCaseErrorReasonSchema,
      workflowCaseReasons,
      t,
    )?.message ??
    resolveAppErrorInfoMessage(
      err,
      PlanSubmissionErrorReasonSchema,
      planSubmissionReasons,
      t,
      submitInterpolation(err),
    )?.message
  )
}

/**
 * 提出が、 画面を開いた後に成立した条件で拒否されたか: AC-PLC-055 / 056 の理由
 * (置き換え済み旧版・箱欠け) と、 AC-PLC-085 の OCCTO の受付時間外。 詳細を再取得して
 * 誘導メッセージや押せない理由へ切り替えるかどうかの判定に使う。
 */
export function shouldRefetchDetailOnSubmitError(err: ConnectError): boolean {
  const reason = readAppErrorInfo(err, PlanSubmissionErrorReasonSchema)?.reason
  return (
    reason === PlanSubmissionErrorReason.SUPERSEDED_VERSION ||
    reason === PlanSubmissionErrorReason.MISSING_BASELINE_BOXES ||
    reason === PlanSubmissionErrorReason.DIRECT_SUBMIT_HELD
  )
}
